import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Provider as NiceModalProvider } from '@ebay/nice-modal-react';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';

import 'typeface-roboto';
import { ThemeSwitcherProvider } from 'react-css-theme-switcher';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import { SWRConfig } from 'swr';

import App from 'containers/App';

import * as serviceWorker from './serviceWorker';
import { store } from './store';
import './utils/i18n';

const DASHBOARD_BASENAME = '/dashboard';

const themes = {
  rush: `${process.env.PUBLIC_URL}/rush-theme.css`,
  zeal: `${process.env.PUBLIC_URL}/zeal-theme.css`,
  affirm: `${process.env.PUBLIC_URL}/affirm-theme.css`,
  light: `${process.env.PUBLIC_URL}/light-theme.css`
};

if (process.env.NODE_ENV !== 'development' && process.env.NODE_ENV !== 'test') {
  Sentry.init({
    dsn: 'https://0d9d3518ae864fca938bf64f646fa78f@sentry.io/1458950',
    environment: process.env.NODE_ENV
  });
}

if (process.env.NODE_ENV === 'development') {
  const whyDidYouRender = require('@welldone-software/why-did-you-render');
  whyDidYouRender(React);
}

const getDefaultTheme = () => {
  let defaultTheme = 'light';
  const hostname = window.location.hostname;

  if (hostname.includes('zeal') || hostname.includes('bltz')) {
    defaultTheme = 'zeal';
  }
  if (hostname.includes('rush')) {
    defaultTheme = 'rush';
  }

  return defaultTheme;
};

const container = document.getElementById('root') || document.createElement('div');
const root = ReactDOM.createRoot(container);

const ensureDashboardInUrl = () => {
  const { pathname, search, hash } = window.location;

  // Normalize `/dashboard` -> `/dashboard/` so Router basename stripping works as expected.
  if (pathname === DASHBOARD_BASENAME) {
    window.history.replaceState({}, document.title, `${DASHBOARD_BASENAME}/${search}${hash}`);
    return;
  }

  if (pathname.startsWith(`${DASHBOARD_BASENAME}/`)) {
    return;
  }

  const basePath = pathname === '/' ? '' : pathname;
  const nextUrl = `${DASHBOARD_BASENAME}${basePath}${search}${hash}`;
  window.history.replaceState({}, document.title, nextUrl);
};

ensureDashboardInUrl();

root.render(
  <HelmetProvider>
    <ThemeSwitcherProvider
      themeMap={themes}
      defaultTheme={getDefaultTheme()}
      insertionPoint="styles-insertion-point"
    >
      <Provider store={store}>
        <SWRConfig value={{ provider: () => new Map(), revalidateOnFocus: false }}>
          <Helmet>
            <title>Perx</title>
          </Helmet>

          <BrowserRouter basename={DASHBOARD_BASENAME}>
            <QueryParamProvider adapter={ReactRouter6Adapter}>
              <NiceModalProvider>
                <App />
              </NiceModalProvider>
            </QueryParamProvider>
          </BrowserRouter>
        </SWRConfig>
      </Provider>
    </ThemeSwitcherProvider>
  </HelmetProvider>
);

serviceWorker.register(null);
